// AI Insights component - displays daily recommendations from Claude
// Shows on Executive Dashboard


/**
 * 
 * NOTES-SUMMARY-AI INSIGHTS
 * 
 * What this component does:
 *  - Shows daily insights from Claude AI
 *  - Displays date insights were generated
 *  - Maps through array of insights and displays each one
 *  - Shows empty state if no insights

 *What is .map()?
 *  .map() loops through an array and does something with each item. Here, it creates an <li> for each insight.

 * 
 */


import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBookmark, faTrash } from '@fortawesome/free-solid-svg-icons';
import Modal from '../common/Modal';
import { formatDate } from '../../utils/formatters';
import { saveInsight, getSavedInsights, deleteInsight } from '../../utils/api';
import '../../styles/components.css';

function AIInsights({ insights, date }) {
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' or 'saved'
  const [savedInsights, setSavedInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Load saved insights when component mounts or when switching to saved tab
  useEffect(() => {
    if (activeTab === 'saved') {
      loadSavedInsights();
    }
  }, [activeTab]);

  const loadSavedInsights = async () => {
    setIsLoading(true);
    try {
      const insights = await getSavedInsights();
      setSavedInsights(insights || []);
    } catch (error) {
      console.error('Error loading saved insights:', error);
      setSavedInsights([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveInsight = async (insight) => {
    setIsLoading(true);
    try {
      const savedInsight = await saveInsight(insight, date);
      // Add to local state immediately for better UX
      setSavedInsights(prev => [savedInsight, ...prev]);
    } catch (error) {
      console.error('Error saving insight:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save insight. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteInsight = async (insightId) => {
    setIsLoading(true);
    try {
      await deleteInsight(insightId);
      // Remove from local state immediately for better UX
      setSavedInsights(prev => prev.filter(insight => insight.id !== insightId));
    } catch (error) {
      console.error('Error deleting insight:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete insight. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  // If no insights, show loading or empty state
  if (!insights || insights.length === 0) {
    return (
      <div className="ai-insights-card">
        <div className="ai-insights-header">
          <h3>
            <span className="sidebar-icon"><FontAwesomeIcon icon={faRobot} /></span>
            AI Insights
            <span className="ai-badge">AI</span>
          </h3>
        </div>
        <div className="ai-insights-tabs">
          <button 
            className={`tab-button ${activeTab === 'daily' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily')}
          >
            Daily Insights
          </button>
          <button 
            className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            <i className="far fa-bookmark"></i> Saved Insights
          </button>
        </div>
        
        {activeTab === 'daily' ? (
          <p className="ai-insight-text">No insights available yet.</p>
        ) : (
          <div className="saved-insights-empty">
            <i className="far fa-bookmark" style={{ fontSize: '48px', opacity: 0.3 }}></i>
            <p>No saved insights yet</p>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="ai-insights-card">
      <div className="ai-insights-header">
        <h3>
          <span className="sidebar-icon"><FontAwesomeIcon icon={faRobot} /></span>
          AI Insights
          <span className="ai-badge">AI</span>
        </h3>
        <span className="ai-date">{formatDate(date)}</span>
      </div>
      
      <div className="ai-insights-tabs">
        <button 
          className={`tab-button ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Daily Insights
        </button>
        <button 
          className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          <i className="far fa-bookmark"></i> Saved Insights
        </button>
      </div>
      
      {activeTab === 'daily' ? (
        <div className="ai-insights-list">
          {insights.map((insight, index) => (
            <div key={index} className="ai-insight-item">
              <div className="ai-insight-text">{insight}</div>
              <div className="ai-insight-actions">
                <button 
                  className="save-insight-btn"
                  onClick={() => handleSaveInsight(insight)}
                  disabled={isLoading}
                  title="Save insight"
                >
                  <FontAwesomeIcon icon={faBookmark} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        savedInsights.length === 0 ? (
          <div className="saved-insights-empty">
            <i className="far fa-bookmark" style={{ fontSize: '48px', opacity: 0.3 }}></i>
            <p>No saved insights yet</p>
          </div>
        ) : (
          <div className="ai-insights-list">
            {savedInsights.map((insight) => (
              <div key={insight.id} className="ai-insight-item saved-insight">
                <div className="ai-insight-text">{insight.text}</div>
                <div className="saved-insight-meta">
                  <span className="saved-date">Saved {formatDate(insight.savedAt)}</span>
                  <span className="original-date">From {formatDate(insight.originalDate)}</span>
                </div>
                <div className="ai-insight-actions">
                  <button 
                    className="delete-insight-btn"
                    onClick={() => handleDeleteInsight(insight.id)}
                    disabled={isLoading}
                    title="Delete insight"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
      
      {/* Error Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
}

export default AIInsights;